"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUserSummary } from "@/lib/api/user";
import { useI18nDebug } from "@/lib/i18n-debug";
import { buildExportFiles, flattenKeySet, lookupKey, useI18nEdit, type Messages } from "@/lib/i18n-edit";
type Resolved = {
  key: string;
  rect: DOMRect;
};

/**
 * Find the text node under a viewport point and, if its (trimmed) text is a
 * known message key, return the key plus the node's bounding rect. Uses the
 * caret-from-point APIs (named differently across engines) because in key-debug
 * mode the visible text of every translated string *is* its dotted key, so
 * there's nothing to wrap at the call sites.
 */
function resolveAt(x: number, y: number, validKeys: Set<string>): Resolved | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => {
      offsetNode: Node;
    } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let node: Node | null = null;
  if (doc.caretPositionFromPoint) {
    node = doc.caretPositionFromPoint(x, y)?.offsetNode ?? null;
  } else if (doc.caretRangeFromPoint) {
    node = doc.caretRangeFromPoint(x, y)?.startContainer ?? null;
  }
  if (!node || node.nodeType !== Node.TEXT_NODE) return null;
  const text = (node.textContent ?? "").trim();
  if (!validKeys.has(text)) return null;
  const range = document.createRange();
  range.selectNodeContents(node);
  return {
    key: text,
    rect: range.getBoundingClientRect()
  };
}

/** True for events originating inside our own editor UI (toolbar/dialog). */
function fromEditorUi(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest("[data-i18n-edit-ui]");
}
function download(filename: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], {
    type: "application/json"
  }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
type Props = {
  /** The active locale's (German-fallback-merged) source messages. */
  messages?: Messages;
  locale?: string;
};

/**
 * Renders nothing unless the viewer is a site admin and key-debug mode is on.
 * In that mode it makes every on-screen key clickable (opening an edit dialog),
 * draws a hover outline so keys read as links, and shows a floating toolbar for
 * searching keys, clearing, and exporting the per-locale override files.
 */
export function I18nEditOverlay(t0) {
  const $ = _c(76);
  const {
    messages,
    locale
  } = t0;
  const summary = useUserSummary();
  const isAdmin = !!summary.data?.user?.is_admin;
  const debug = useI18nDebug(_temp);
  const setDebugEnabled = useI18nDebug(_temp2);
  const keyMode = isAdmin && debug && !!messages && !!locale;
  const overrides = useI18nEdit(_temp3);
  const setOverride = useI18nEdit(_temp4);
  const clearOverride = useI18nEdit(_temp5);
  const clearAll = useI18nEdit(_temp6);
  let t1;
  if ($[0] !== messages) {
    t1 = messages ? flattenKeySet(messages) : new Set();
    $[0] = messages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const validKeys = t1;
  const [hover, setHover] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const rafRef = useRef(null);
  let t2;
  if ($[2] !== locale || $[3] !== messages || $[4] !== overrides) {
    t2 = key => {
      const current = (locale && overrides[locale]?.[key]) ?? (messages ? lookupKey(messages, key) : undefined) ?? "";
      setDraft(current);
      setEditingKey(key);
      setSearch("");
    };
    $[2] = locale;
    $[3] = messages;
    $[4] = overrides;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const openKey = t2;
  let t3;
  let t4;
  if ($[6] !== keyMode || $[7] !== openKey || $[8] !== setDebugEnabled || $[9] !== validKeys) {
    t3 = () => {
      if (!keyMode) {
        return;
      }
      const onClick = e => {
        if (fromEditorUi(e.target)) {
          return;
        }
        const hit = resolveAt(e.clientX, e.clientY, validKeys);
        if (!hit) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        setDebugEnabled(false);
        setHover(null);
        openKey(hit.key);
      };
      window.addEventListener("click", onClick, {
        capture: true
      });
      return () => window.removeEventListener("click", onClick, {
        capture: true
      });
    };
    t4 = [keyMode, validKeys, openKey, setDebugEnabled];
    $[6] = keyMode;
    $[7] = openKey;
    $[8] = setDebugEnabled;
    $[9] = validKeys;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[12] !== keyMode || $[13] !== validKeys) {
    t5 = () => {
      if (!keyMode) {
        return;
      }
      const onMove = e_0 => {
        if (rafRef.current != null) {
          return;
        }
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          if (fromEditorUi(e_0.target)) {
            setHover(null);
            return;
          }
          setHover(resolveAt(e_0.clientX, e_0.clientY, validKeys)?.rect ?? null);
        });
      };
      const onScroll = () => setHover(null);
      window.addEventListener("mousemove", onMove);
      window.addEventListener("scroll", onScroll, true);
      return () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("scroll", onScroll, true);
        if (rafRef.current != null) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = null;
      };
    };
    t6 = [keyMode, validKeys];
    $[12] = keyMode;
    $[13] = validKeys;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  if (!isAdmin || !messages || !locale || !keyMode && editingKey == null) {
    return null;
  }
  let t7;
  if ($[16] !== locale || $[17] !== overrides) {
    t7 = overrides[locale] ?? {};
    $[16] = locale;
    $[17] = overrides;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const localeOverrides = t7;
  let t8;
  if ($[19] !== localeOverrides) {
    t8 = Object.keys(localeOverrides);
    $[19] = localeOverrides;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const localeCount = t8.length;
  let t9;
  if ($[21] !== overrides) {
    t9 = Object.values(overrides).reduce(_temp7, 0);
    $[21] = overrides;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const totalCount = t9;
  const isOverridden = editingKey != null && editingKey in localeOverrides;
  let t10;
  if ($[23] !== search || $[24] !== validKeys) {
    t10 = search.trim() ? [...validKeys].filter(k => k.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 20) : [];
    $[23] = search;
    $[24] = validKeys;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const searchResults = t10;
  let t11;
  if ($[26] !== overrides) {
    t11 = () => {
      const files = buildExportFiles(overrides);
      if (files.length === 0) {
        toast("No overrides to export");
        return;
      }
      files.forEach(_temp8);
      toast(`Exported ${files.length} file${files.length === 1 ? "" : "s"}`);
    };
    $[26] = overrides;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const onExport = t11;
  let t12;
  if ($[28] !== hover || $[29] !== keyMode) {
    t12 = keyMode && hover && <div style={{
      position: "fixed",
      left: hover.left - 2,
      top: hover.top - 2,
      width: hover.width + 4,
      height: hover.height + 4,
      border: "1px dashed var(--primary)",
      borderRadius: 3,
      background: "color-mix(in srgb, var(--primary) 12%, transparent)",
      pointerEvents: "none",
      zIndex: 2147483000
    }} />;
    $[28] = hover;
    $[29] = keyMode;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== clearAll || $[32] !== keyMode || $[33] !== locale || $[34] !== localeCount || $[35] !== onExport || $[36] !== openKey || $[37] !== search || $[38] !== searchResults || $[39] !== totalCount) {
    t13 = keyMode && <div data-i18n-edit-ui={true} className="fixed bottom-4 right-4 z-[2147483001] flex max-w-[22rem] flex-col gap-2 rounded-lg border bg-background/95 p-3 text-sm shadow-lg backdrop-blur"><div className="flex items-center justify-between gap-2"><span className="font-medium">i18n edit · {locale}</span><span className="text-muted-foreground">{localeCount} here / {totalCount} total</span></div><Input value={search} onChange={e_1 => setSearch(e_1.target.value)} placeholder={"Search key\u2026"} className="h-8" />{searchResults.length > 0 && <div className="max-h-48 overflow-auto rounded border">{searchResults.map(key_0 => <button key={key_0} type="button" onClick={() => openKey(key_0)} className="block w-full truncate px-2 py-1 text-left font-mono text-xs hover:bg-accent">{key_0}</button>)}</div>}<div className="flex gap-2"><Button size="sm" className="flex-1" onClick={onExport}>Export</Button><Button size="sm" variant="outline" disabled={totalCount === 0} onClick={() => clearAll()}>Clear all</Button></div><p className="text-xs text-muted-foreground">Click any highlighted key to edit. Toggle with Ctrl+Alt+K.</p></div>;
    $[31] = clearAll;
    $[32] = keyMode;
    $[33] = locale;
    $[34] = localeCount;
    $[35] = onExport;
    $[36] = openKey;
    $[37] = search;
    $[38] = searchResults;
    $[39] = totalCount;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const t14 = editingKey != null;
  let t15;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = open => {
      if (!open) {
        setEditingKey(null);
      }
    };
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DialogTitle>Edit translation</DialogTitle>;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== editingKey || $[44] !== locale) {
    t17 = <DialogHeader>{t16}<DialogDescription className="break-all font-mono text-xs">{editingKey} · {locale}</DialogDescription></DialogHeader>;
    $[43] = editingKey;
    $[44] = locale;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = e_2 => setDraft(e_2.target.value);
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== draft) {
    t19 = <Textarea value={draft} onChange={t18} rows={5} autoFocus={true} />;
    $[47] = draft;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const t20 = !isOverridden;
  let t21;
  if ($[49] !== clearOverride || $[50] !== editingKey || $[51] !== locale) {
    t21 = () => {
      if (editingKey) {
        clearOverride(locale, editingKey);
      }
      setEditingKey(null);
    };
    $[49] = clearOverride;
    $[50] = editingKey;
    $[51] = locale;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t20 || $[54] !== t21) {
    t22 = <Button variant="ghost" disabled={t20} onClick={t21}>Reset</Button>;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Button variant="outline" onClick={() => setEditingKey(null)}>Cancel</Button>;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== draft || $[58] !== editingKey || $[59] !== locale || $[60] !== setOverride) {
    t24 = <div className="flex gap-2">{t23}<Button onClick={() => {
        if (editingKey) {
          setOverride(locale, editingKey, draft);
        }
        setEditingKey(null);
      }}>Save</Button></div>;
    $[57] = draft;
    $[58] = editingKey;
    $[59] = locale;
    $[60] = setOverride;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t22 || $[63] !== t24) {
    t25 = <DialogFooter className="gap-2 sm:justify-between">{t22}{t24}</DialogFooter>;
    $[62] = t22;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t17 || $[66] !== t19 || $[67] !== t25) {
    t26 = <DialogContent data-i18n-edit-ui={true}>{t17}{t19}{t25}</DialogContent>;
    $[65] = t17;
    $[66] = t19;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== t14 || $[70] !== t26) {
    t27 = <Dialog open={t14} onOpenChange={t15}>{t26}</Dialog>;
    $[69] = t14;
    $[70] = t26;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t12 || $[73] !== t13 || $[74] !== t27) {
    t28 = <>{t12}{t13}{t27}</>;
    $[72] = t12;
    $[73] = t13;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  return t28;
}
function _temp8(f) {
  return download(f.filename, f.json);
}
function _temp7(sum, keys) {
  return sum + Object.keys(keys).length;
}
function _temp6(s_4) {
  return s_4.clearAll;
}
function _temp5(s_3) {
  return s_3.clearOverride;
}
function _temp4(s_2) {
  return s_2.setOverride;
}
function _temp3(s_1) {
  return s_1.overrides;
}
function _temp2(s_0) {
  return s_0.setEnabled;
}
function _temp(s) {
  return s.enabled;
}